import { formatIsoDateLocale } from "./dateUtils";

/**
 * `detail_params` keys the backend sends as ISO dates. Listed explicitly rather than sniffed: a value
 * that merely looks like a date must not be silently reformatted, and a new date param should be a
 * deliberate addition on both sides.
 *
 * `week` is the "week of" label on the PREDICTED_OT badge; `date` covers the replan no-shift reason and
 * the BEYOND_HORIZON badge.
 */
const ISO_DATE_PARAM_KEYS = new Set(["date", "week"]);

/**
 * A full ISO calendar date — the exact shape `date.isoformat()` produces.
 *
 * The key allowlist alone is not enough, because `DateTime.fromISO` accepts far more than a date:
 * `"12"` is a valid ISO *hour* and resolves to TODAY at noon, and `"2026"` resolves to Jan 1. Either
 * would render a confidently wrong date rather than failing visibly. Matching the shape first means
 * anything else falls through to the raw value, which is the honest outcome.
 */
const ISO_DATE_SHAPE = /^\d{4}-\d{2}-\d{2}(T|$)/;

/**
 * Localizes the ISO date values in a `detail_params` bag before it reaches `t()`.
 *
 * i18next interpolates params verbatim, so a date the backend formatted itself renders in ITS language
 * inside the reader's sentence — an English "Sep 2" dropped into the Spanish and Hebrew
 * `annie.replan.no_shift_on_date`. The backend therefore sends locale-neutral ISO and the date becomes
 * display copy here, where the active locale is known.
 *
 * Non-date keys and anything that is not a full ISO date pass through untouched, so a param that is
 * already display-formatted is left exactly as it arrives rather than mangled into a wrong date.
 */
export const localizeAnnieDetailParams = (params?: Record<string, string> | null): Record<string, string> => {
	if (!params) return {};
	return Object.fromEntries(
		Object.entries(params).map(([key, value]) => [key, ISO_DATE_PARAM_KEYS.has(key) && ISO_DATE_SHAPE.test(value) ? formatIsoDateLocale(value) : value]),
	);
};
